/**
 * The caller-ID device on the telephone line (kassa-analyse: Donass uses a USB modem with caller ID,
 * "Conexant USB CX…"; besluiten: the new till must support it). Such a modem speaks AT commands: it is
 * asked once to report the caller's number, and then writes, between the first and second ring:
 *
 *   RING
 *   DATE = 1001
 *   TIME = 1845
 *   NMBR = 0472184490
 *
 * "NMBR = P" is a hidden (private) number, "NMBR = O" one that is not available.
 */

/**
 * How the number comes over the line differs per country and per box the line hangs on (the exchange,
 * or the modem of the internet provider): before or after the first ring, as tones or as data. A modem
 * listens the way of the country it is set to (T.35 code, AT+GCI). Belgium first; when a call rings
 * without a number, the next way is tried at the following call, until one gives a number (Papermoon,
 * 07/10/2026: RING but no number with Belgium). 0F Belgium, B5 United States, 42 Germany, 7B the
 * Netherlands, B4 United Kingdom.
 */
export const COUNTRIES = ["0F", "B5", "42", "7B", "B4"] as const;

/**
 * Asked once, in this order: reset, no echo, the country and formatted caller ID. A modem that does
 * not know AT+VCID gets the older AT#CID, and after that AT%CCID.
 */
export const setupFor = (country: string): string[] => ["ATZ", "ATE0", `AT+GCI=${country}`, "AT+VCID=1"];
export const SETUP = setupFor(COUNTRIES[0]);
const FALLBACK: Record<string, string> = { "AT+VCID=1": "AT#CID=1", "AT#CID=1": "AT%CCID=1" };
/** A modem that does not answer a command in time is not waited for. */
export const ANSWER_MS = 2_500;
/** After an answer the modem gets a moment: right after a reset (ATZ) some lose the next command. */
export const PAUSE_MS = 400;
/** No ring for this long: the call is over. */
export const CALL_OVER_MS = 8_000;
/** A line that is part of what a modem writes about the caller. */
const CALLER_LINE = /^\s*(?:NMBR|NUMBER|DDN_NMBR|DATE|TIME|NAME|MESG|CALLER\s+NUMBER|CID|CLIP|\+CLIP)\s*[=:]/i;

/** Which way of listening this modem uses, and whether a call ever came with a number that way. */
export interface Way {
  country: number;
  proven: boolean;
}

/**
 * The caller's number in a line of the modem; null for a hidden number or any other line. Most modems
 * write "NMBR = 0472184490"; some "CALLER NUMBER: …", "+CLIP: "0472184490",129" or "CID: …".
 */
export function numberInLine(line: string): string | null {
  const match = /^\s*(?:NMBR|NUMBER|DDN_NMBR|CALLER\s+NUMBER|CID|CLIP|\+CLIP)\s*[=:]\s*"?([^",]*)/i.exec(line);
  const digits = (match?.[1] ?? "").replace(/\D/g, "");
  return digits.length >= 3 ? digits : null;
}

/** What went over the line, for the person who sets the device up (Beheer shows the last lines). */
export type Trace = (direction: "out" | "in", text: string) => void;

/**
 * One connection to the modem: sends the setup commands one by one (each after the answer to the
 * previous one, or after a short wait), then reports every caller's number.
 */
export class ModemSession {
  private buffer = "";
  private readonly commands: string[];
  private way: Way;
  /** Something about the caller came with the call that is ringing now. */
  private callerSeen = false;
  private callTimer: ReturnType<typeof setTimeout> | undefined;
  /** The number told last, and when this call was last heard (a ring, or its number again). */
  private told: { number: string; heardAt: number } | null = null;
  private step = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private stopped = false;

  /** A command was sent and its answer is awaited. */
  private pending = false;
  /** The modem answered a command at least once: it is a modem, and it listens. */
  private answered = false;

  constructor(
    private readonly write: (text: string) => void,
    private readonly onNumber: (digits: string) => void,
    private readonly trace: Trace = () => undefined,
    /** Told once, at the first answer of the modem. */
    private readonly onAnswer: () => void = () => undefined,
    /** The way to start with (what this till remembered), and where a change is told. */
    way: Way = { country: 0, proven: false },
    private readonly onWay: (way: Way) => void = () => undefined,
  ) {
    this.way = { country: way.country % COUNTRIES.length, proven: way.proven };
    this.commands = setupFor(COUNTRIES[this.way.country]!);
  }

  start(): void {
    this.send();
  }

  stop(): void {
    this.stopped = true;
    clearTimeout(this.timer);
    clearTimeout(this.callTimer);
  }

  /** A call rang out without anything about the caller: the next way of listening, for the next call. */
  private callOver(): void {
    if (this.stopped || this.callerSeen || this.way.proven) return;
    this.way = { country: (this.way.country + 1) % COUNTRIES.length, proven: false };
    this.onWay(this.way);
    this.commands.push(`AT+GCI=${COUNTRIES[this.way.country]!}`, "AT+VCID=1");
    if (!this.pending) this.send();
  }

  /** Text as it comes from the modem, in pieces of any size. */
  feed(text: string): void {
    this.buffer += text;
    const lines = this.buffer.split(/\r\n|\r|\n/);
    this.buffer = lines.pop() ?? "";
    for (const line of lines) this.line(line.trim());
  }

  private send(): void {
    if (this.stopped || this.step >= this.commands.length) return;
    this.trace("out", this.commands[this.step]!);
    this.write(`${this.commands[this.step]!}\r`);
    this.pending = true;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.next(false);
    }, ANSWER_MS);
  }

  /** The answer to the current command: OK, ERROR, or none in time. */
  private next(ok: boolean): void {
    const command = this.commands[this.step];
    const fallback = command === undefined ? undefined : FALLBACK[command];
    if (!ok && fallback !== undefined) this.commands.splice(this.step + 1, 0, fallback);
    this.step++;
    this.pending = false;
    if (!ok) {
      this.send();
      return;
    }
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.send();
    }, PAUSE_MS);
  }

  private line(line: string): void {
    if (line === "") return;
    this.trace("in", line);
    if (line === "OK" || line === "ERROR") {
      if (!this.answered) {
        this.answered = true;
        this.onAnswer();
      }
      if (this.pending) {
        clearTimeout(this.timer);
        this.next(line === "OK");
      }
      return;
    }
    if (/^RING\b/i.test(line)) {
      // one more ring of the call that is ringing (not the first ring of a new one)
      if (this.told && this.callTimer !== undefined) this.told.heardAt = Date.now();
      // the first ring of a call, or one more: the call is over when they stop
      if (this.callTimer === undefined) this.callerSeen = false;
      clearTimeout(this.callTimer);
      this.callTimer = setTimeout(() => {
        this.callTimer = undefined;
        this.callOver();
      }, CALL_OVER_MS);
      return;
    }
    if (CALLER_LINE.test(line)) {
      this.callerSeen = true;
      if (!this.way.proven) {
        // also a hidden number: the line and the modem understand each other this way
        this.way = { ...this.way, proven: true };
        this.onWay(this.way);
      }
    }
    const number = numberInLine(line);
    if (number === null) return;
    // a modem that writes the number at every ring: one call is told once. A call that ended and the
    // same number calling again is a new call.
    const now = Date.now();
    const again = this.told?.number === number && now - this.told.heardAt < CALL_OVER_MS;
    this.told = { number, heardAt: now };
    if (!again) this.onNumber(number);
  }
}
